import { useMemo } from 'react';
import { LayoutList } from 'lucide-react';
import type { ActivityItem, ActivityTriggerSource } from '@/types';
import { routes } from '@/lib/routes';
import { formatRelativeTime } from '@/pages/activity/utils';
import { useMeAgents as useAgents } from '@/hooks/useAgents';
import { OverviewPanel } from './OverviewPanel';
import { cleanDisplayText } from '@/utils/display-text';

interface RecentActivitiesPanelProps {
  items?: ActivityItem[];
  isLoading?: boolean;
}

const MAX = 10;

const TRIGGER_SOURCE_LABELS: Record<ActivityTriggerSource, string> = {
  user: 'User',
  email: 'Email',
  scheduler: 'Scheduler',
  webhook: 'Webhook',
  api: 'API',
  event: 'Event',
};

const TRIGGER_SOURCE_COLORS: Record<ActivityTriggerSource, string> = {
  user: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  email: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  scheduler: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  webhook: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  api: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  event: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
};

export function RecentActivitiesPanel({ items, isLoading }: RecentActivitiesPanelProps) {
  const list = (items ?? []).slice(0, MAX);
  const { data: agentsData } = useAgents();
  const agentNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const agent of agentsData?.items ?? []) {
      map.set(agent.agentId, agent.name);
    }
    return map;
  }, [agentsData]);

  return (
    <OverviewPanel
      title="Recent Activity"
      icon={<LayoutList className="h-5 w-5" />}
      action={{ label: 'View all', to: routes.operator.activity }}
      isLoading={isLoading && !items}
      isEmpty={!isLoading && list.length === 0}
      emptyMessage="No activity yet"
      bodyClassName="p-0"
    >
      <ul className="divide-y divide-border">
        {list.map((activity) => {
          const trigger = activity.triggerSource;
          const agentId = activity.relatedEntityIds?.agentId;
          const agentName = agentId ? agentNameById.get(agentId) : undefined;
          return (
            <li
              key={activity.activityId}
              className="flex items-start justify-between gap-3 px-4 py-2.5"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm text-foreground truncate">
                    {cleanDisplayText(activity.title) || activity.category || 'Untitled activity'}
                  </p>
                  {trigger && (
                    <span
                      className={
                        'shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ' +
                        (TRIGGER_SOURCE_COLORS[trigger] ??
                          'bg-muted text-muted-foreground')
                      }
                    >
                      {TRIGGER_SOURCE_LABELS[trigger] ?? trigger}
                    </span>
                  )}
                </div>
                {agentName && (
                  <p className="text-xs text-muted-foreground truncate">
                    {agentName}
                  </p>
                )}
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0 mt-0.5">
                {formatRelativeTime(activity.createdAt)}
              </span>
            </li>
          );
        })}
      </ul>
    </OverviewPanel>
  );
}
